import { Alert, Box, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartData,
  type ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useMemo } from 'react'
import { EmissionTier, MetricState } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend)

type WindPoint = { x: number; y: number }

const SERIES_COLORS = ['#1976d2', '#2e7d32', '#d32f2f', '#7b1fa2', '#ffb300']

function formatSpeed(value: number | null | undefined) {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return `${value.toFixed(1)} m/s`
}

function normalizeSeries(entry: unknown): WindPoint[] {
  if (!entry) return []

  if (Array.isArray(entry)) {
    return entry
      .map(item => {
        const raw = item as any
        const x = typeof raw?.x === 'number' ? raw.x : null
        const y = typeof raw?.y === 'number' ? raw.y : null
        return x != null && y != null ? { x, y } : null
      })
      .filter((p): p is WindPoint => p !== null)
      .sort((a, b) => a.x - b.x)
  }

  if (typeof entry === 'object' && entry) {
    const x = typeof (entry as any).x === 'number' ? (entry as any).x : null
    const y = typeof (entry as any).y === 'number' ? (entry as any).y : null
    if (x != null && y != null) {
      return [{ x, y }]
    }
    if (Array.isArray((entry as any).data)) {
      return normalizeSeries((entry as any).data)
    }
  }

  return []
}

type Props = {
  propertyId: number
  emissionTier: EmissionTier
}

function ExtremeWindSpeedTrends({ propertyId, emissionTier }: Props) {
  const { data: windSpeedData, isLoading, error } = usePropertyClimateData('tc:wind_speed', { propertyId })

  const loading = isLoading && !windSpeedData

  const tierData = windSpeedData?.data?.data?.[emissionTier] as Record<string, unknown> | undefined

  const returnPeriods =
    (windSpeedData?.data?.keys?.return_period as string[] | undefined) ?? Object.keys(tierData ?? {})

  const seriesByReturnPeriod = useMemo(() => {
    return returnPeriods.reduce<Record<string, WindPoint[]>>((acc, rp) => {
      acc[rp] = normalizeSeries(tierData?.[rp])
      return acc
    }, {})
  }, [returnPeriods, tierData])

  const overview = useMemo(() => {
    return returnPeriods.map(rp => {
      const points = seriesByReturnPeriod[rp] || []
      if (!points.length) return { rp, year: null, value: null }
      const latest = points.reduce((best, point) => (!best || point.x > best.x ? point : best), points[0])
      return { rp, year: latest?.x ?? null, value: latest?.y ?? null }
    })
  }, [returnPeriods, seriesByReturnPeriod])

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Unable to load wind speed projections.</Alert>
  }

  if (!windSpeedData) {
    return <Alert severity="info">No wind speed data is available for this property.</Alert>
  }

  if (windSpeedData.state.value !== MetricState.SUCCEEDED) {
    return (
      <Alert severity="warning">Wind speed calculations are unavailable (state: {windSpeedData.state.value}).</Alert>
    )
  }

  if (!tierData) {
    return (
      <Alert severity="info">
        No wind speed projections for emission tier <strong>{emissionTier}</strong>.
      </Alert>
    )
  }

  if (!returnPeriods.length) {
    return <Alert severity="info">Wind speed data does not contain return periods to display.</Alert>
  }

  return (
    <Stack spacing={3}>
      <Box>
        <ClimateSectionTitle
          title="Extreme Wind Speeds"
          subtitle="Average projected extreme wind speed events"
          helpText="Shows wind speed projections by return period for the selected emissions scenario."
        />
      </Box>

      <Grid container spacing={2}>
        {(overview || []).map(item => (
          <Grid item xs={12} sm={4} md={3} key={item.rp}>
            <Box
              p={2}
              border={1}
              borderColor="divider"
              borderRadius={2}
              display="flex"
              flexDirection="column"
              height="100%"
            >
              <Typography variant="subtitle2" color="text.secondary">
                {item.rp}-year event
              </Typography>
              <Typography variant="h5" mt={1}>
                {formatSpeed(item.value)}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Stack>
  )
}

export default ExtremeWindSpeedTrends
